import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  List, 
  Typography, 
  Spin, 
  Alert,
  Button,
  Divider
} from 'antd';
import Setting from '../Setting';
import { 
  DollarOutlined, 
  CreditCardOutlined, 
  CheckOutlined 
} from '@ant-design/icons';
import { PaymentAPI } from '../backend/api';
import { useAuth } from '../utils/auth';

const { Title, Paragraph } = Typography;

const PaymentPage = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await PaymentAPI.getPlans();
      const data = response?.data?.data;
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to fetch subscription plans');
      console.error('Plans fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

       const handlePayment = async () => {
     if (!selectedPlan || !user) return;

     setIsProcessing(true);
     try {
       // Map payment method to provider name
       const providerMap = {
         paypal: 'provider_payment_paypal',
         credit_card: 'provider_payment_paypal' // For now, both use PayPal; extend as needed
       };
       const providerName = providerMap[paymentMethod] || 'provider_payment_paypal';

       const orderRes = await PaymentAPI.createOrder(
         [{
           Name: selectedPlan.product,
           Price: selectedPlan.price,
           Quantity: 1,
           PricingName: 'pricing_bingeit',
           PlanName: selectedPlan.name,
         }],
         user.name
       );
       const order = orderRes?.data?.data;
       if (!order) throw new Error('Failed to create order');

       const payRes = await PaymentAPI.getPaymentUrl(`${order.owner}/${order.name}`, providerName);
       const payUrl = payRes?.data?.data?.payUrl;
       if (!payUrl) throw new Error('Failed to get payment URL');

       window.location.href = payUrl;
     } catch (error) {
       Setting.showMessage('error', 'Payment failed');
       console.error('Payment error:', error);
     } finally {
       setIsProcessing(false);
     }
   };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  const paymentMethods = [
    {
      id: 'paypal',
      icon: <DollarOutlined style={{ fontSize: '32px', color: '#003087' }} />,
      name: 'PayPal',
      description: 'Secure online payment'
    },
    {
      id: 'credit_card',
      icon: <CreditCardOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      name: 'Credit Card',
      description: 'Visa, MasterCard, American Express'
    },
  ];

  return (
    <div>
      <Title level={2}>Subscription Plans</Title>
      <Paragraph>
        Choose the plan that's right for you. All plans include 4K streaming, 
        simultaneous streams, and access to our entire library.
      </Paragraph>

      <Divider orientation="center">Select a Plan</Divider>

      <Row gutter={[16, 16]}>
        {plans.map((plan) => (
          <Col key={plan.id} xs={24} sm={12} md={8} lg={6}>
            <Card 
              hoverable 
              style={{ 
                textAlign: 'center', 
                ...(selectedPlan?.id === plan.id && { border: '2px solid #1890ff' })
              }}
              onClick={() => setSelectedPlan(plan)}
            >
              <Title level={3}>{plan.name}</Title>
               <Title level={1} style={{ color: '#1890ff' }}>
                 {plan.price} kr<span style={{ fontSize: '16px' }}>/{plan.period === 'Yearly' ? 'year' : 'month'}</span>
               </Title>
              <Paragraph>
                {plan.description}
              </Paragraph>
              <List
                dataSource={Array.isArray(plan.features) ? plan.features : []}
                renderItem={(feature) => (
                  <List.Item style={{ justifyContent: 'center' }}>
                    <CheckOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                    {feature}
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {selectedPlan && (
        <>
          <Divider orientation="center">Payment Method</Divider>

          <Row gutter={[16, 16]} style={{ marginTop: '30px' }}>
            {paymentMethods.map((method) => (
              <Col key={method.id} xs={24} sm={12} md={8}>
                <Card
                  hoverable
                  style={{ 
                    textAlign: 'center', 
                    cursor: 'pointer',
                    ...(paymentMethod === method.id && { 
                      border: '2px solid #1890ff',
                      backgroundColor: '#f0f9ff'
                    })
                  }}
                  onClick={() => setPaymentMethod(method.id)}
                >
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>
                    {method.icon}
                  </div>
                  <Title level={4}>{method.name}</Title>
                  <Paragraph>{method.description}</Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          <Divider />

          <div style={{ textAlign: 'center', marginTop: '30px' }}>
             <Title level={3}>
               Total: {selectedPlan.price} kr
             </Title>
            <Paragraph>
              You will be redirected to {paymentMethod === 'paypal' ? 'PayPal' : 'payment gateway'} to complete your payment.
            </Paragraph>
            <Button 
              type="primary" 
              size="large" 
              onClick={handlePayment}
              loading={isProcessing}
              style={{ marginRight: '10px' }}
            >
              Subscribe Now
            </Button>
            <Button onClick={() => setSelectedPlan(null)} disabled={isProcessing}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentPage;